import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Container, Nav } from 'react-bootstrap';
import logo from '../assets/blinkurl-logo.svg';
import { AuthContext } from '../AuthProvider';
import '../css/navbar.css';

function Navigation({ isAuthenticated, onLogout }) {
  const location = useLocation();
  const auth = useContext(AuthContext);
  const resolvedAuth = isAuthenticated ?? auth?.isAuthenticated ?? false;
  const resolvedLogout = onLogout ?? auth?.logout ?? (() => {});
  const navigate = useNavigate();
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const checkStandalone = () => setIsStandalone(mediaQuery.matches || window.navigator.standalone === true);
    checkStandalone();
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    mediaQuery.addEventListener?.('change', checkStandalone);
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      mediaQuery.removeEventListener?.('change', checkStandalone);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setExpanded(false);
    resolvedLogout();
    navigate('/login'); // Redirect to login page after logout
  };

  const handleInstall = async () => {
    if (!installPromptEvent) return;
    setExpanded(false);
    installPromptEvent.prompt();
    await installPromptEvent.userChoice.catch(() => ({}));
    setInstallPromptEvent(null);
  };

  const closeMenu = () => setExpanded(false);
  const reloadPage = () => {
    closeMenu();
    window.location.reload();
  };

  const displayName = useMemo(() => {
    const stored = localStorage.getItem('username') || '';
    if (resolvedAuth && stored) return stored;
    return resolvedAuth ? 'Member' : 'Guest';
  }, [resolvedAuth]);

  const path = location.pathname || '/';
  const knownRoutes = new Set([
    '/',
    '/login',
    '/signup',
    '/dashboard',
    '/new',
    '/shortened-links',
    '/documentation',
    '/about',
    '/features',
    '/pricing',
    '/privacy',
    '/terms',
    '/profile',
    '/support',
    '/news',
    '/blog',
  ]);

  const segments = path.split('/').filter(Boolean);
  const isRedirectRoute =
    segments.length === 1 &&
    !knownRoutes.has(path) &&
    !path.endsWith('/details');

  if (isRedirectRoute) {
    return null;
  }

  return (
    <Navbar
      expand="lg"
      expanded={expanded}
      onToggle={(value) => setExpanded(value)}
      className={`custom-navbar ${scrolled ? 'is-scrolled' : ''}`}
      sticky="top"
    >
      <Container>
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center gap-2">
          <img src={logo} alt="BlinkURL logo" width="34" height="34" />
          <span className="brand-text">
            BlinkURL
            <small>Smart short links</small>
          </span>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto align-items-lg-center">
            <Nav.Link as={Link} to="/" onClick={closeMenu} className={`nav-link-item ${location.pathname === '/' ? 'active' : ''}`}>
              Home
            </Nav.Link>
            <Nav.Link as={Link} to="/features" onClick={closeMenu} className={`nav-link-item ${location.pathname === '/features' ? 'active' : ''}`}>
              Features
            </Nav.Link>
            <Nav.Link as={Link} to="/pricing" onClick={closeMenu} className={`nav-link-item ${location.pathname === '/pricing' ? 'active' : ''}`}>
              Pricing
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/news"
              onClick={closeMenu}
              className={`nav-link-item ${location.pathname.startsWith('/news') ? 'active' : ''}`}
            >
              News
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/blog"
              onClick={closeMenu}
              className={`nav-link-item ${location.pathname.startsWith('/blog') ? 'active' : ''}`}
            >
              Blog
            </Nav.Link>
            {!isStandalone && installPromptEvent && (
              <Nav.Link onClick={handleInstall} className="nav-link-item">
                Install App
              </Nav.Link>
            )}
            {!resolvedAuth ? (
              <>
                <Nav.Link as={Link} to="/login" onClick={closeMenu} className="nav-link-item">
                  Login
                </Nav.Link>
                <Nav.Link as={Link} to="/signup" onClick={closeMenu} className="nav-link-item nav-cta">
                  Get Started
                </Nav.Link>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/dashboard" onClick={closeMenu} className="nav-link-item">
                  Dashboard
                </Nav.Link>
                <Nav.Link as={Link} to="/profile" onClick={closeMenu} className="nav-link-item">
                  Profile
                </Nav.Link>
                <Nav.Link onClick={handleLogout} className="nav-link-item">
                  Logout
                </Nav.Link>
              </>
            )}
            {resolvedAuth && <span className="nav-status">Signed in as {displayName}</span>}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default Navigation;
